import { createContext } from 'react';
import type { OfficerIdentityDto } from '@pams/shared';

/**
 * Context and types only — no component, no hook.
 *
 * Three files (`lib/authContext.ts`, `components/AuthProvider.tsx`, `hooks/useAuth.ts`)
 * rather than one, so `react-refresh/only-export-components` never fires and this needs
 * no ESLint exception — unlike `theme/ThemeProvider.tsx`, which has one.
 */
export type AuthStatus = 'bootstrapping' | 'anonymous' | 'authenticated';

export type AuthContextValue = {
  status: AuthStatus;
  officer: OfficerIdentityDto | undefined;
  signIn: (session: { accessToken: string; refreshToken: string; officer: OfficerIdentityDto }) => void;
  signOut: () => Promise<void>;
  /**
   * ⚠️ [amended C1a] WHY THE LAST SESSION ENDED, IN PLAIN LANGUAGE, FOR THE LOGIN SCREEN.
   *
   * A revocation used to be a silent `<Navigate to="/login">`: the operator was working,
   * and then they were looking at a sign-in form with nothing to say what had happened or
   * whether it would happen again. `AuthProvider` sets this from the `AuthLostReason` the
   * client hands it.
   *
   * ⚠️ IT LIVES IN REACT STATE, DELIBERATELY NOT IN STORAGE. The provider is constructed
   * per page load, so typing `/login` into the address bar is a fresh provider and shows
   * nothing — which is exactly the "cleared on a deliberate visit" behaviour wanted, for
   * free, rather than as a cleanup somebody has to remember.
   *
   * ⚠️ BOTH KEYS ARE OPTIONAL. `web/src/test/renderWithProviders.tsx` and `pwa`'s
   * equivalent build this value as OBJECT LITERALS, so a required field would break every
   * test that renders through them — for a field none of them care about.
   */
  signedOutReason?: string;
  clearSignedOutReason?: () => void;
};

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);
